import { defineStore } from 'pinia'
import httpClient from '@/api/httpClient'
import { toKeyValObject } from '@/utils/state_helpers'

import { getDefaultPaginationState } from '@/stores/shared/pagination'
import { useMessagesStore } from '@/stores/messages'

export const useBylinesStore = defineStore('bylines', {
  state: () => ({
    ...getDefaultPaginationState(),
    bylines: []
  }),

  getters: {
    asObject: ({ bylines }) => {
      return toKeyValObject(bylines, 'web_content', 'id')
    },

    hasMultiple() {
      return this.bylines.length > 1
    }
  },

  actions: {
    async search({ page, perPage, publicationId, searchQuery } = {}) {
      return await httpClient.get('/api/internal/bylines', {
        params: {
          search: searchQuery,
          publication_id: publicationId,
          page,
          per_page: perPage
        }
      }).then(({ data: { bylines, meta: { page }} }) => {
        this.bylines = bylines
        this.page = page
      })
      .catch((err) => {
        useMessagesStore().smartAdd(err)
      })
    },

    async sync({ publicationId }) {
      return httpClient.post(`/api/internal/publications/${publicationId}/bylines`)
        .then(({ data: { bylines } }) => {
          this.bylines = bylines
          useMessagesStore().addNotice('Bylines synced')
        })
        .catch((err) => {
          useMessagesStore().addError(err)
        })
    },

    async create(bylineData) {
      return httpClient.post('/api/internal/bylines', { byline: bylineData })
        .then(({ data: { byline } }) => {
          this.bylines.push(byline)
          useMessagesStore().addNotice('Byline created')
          return byline
        })
        .catch((err) => {
          useMessagesStore().smartAdd(err)
          throw err
        })
    },

    async update({ id, ...bylineData }) {
      return httpClient.patch(`/api/internal/bylines/${id}`, { byline: bylineData })
        .then(({ data: { byline } }) => {
          const index = this.bylines.findIndex(b => b.id === id)
          if (index !== -1) {
            this.bylines[index] = byline
          }
          useMessagesStore().addNotice('Byline updated')
          return byline
        })
        .catch((err) => {
          useMessagesStore().smartAdd(err)
          throw err
        })
    },

    async destroy(id) {
      return httpClient.delete(`/api/internal/bylines/${id}`)
        .then(() => {
          const index = this.bylines.findIndex(b => b.id === id)
          if (index !== -1) {
            this.bylines.splice(index, 1)
          }
          useMessagesStore().addNotice('Byline archived')
        })
        .catch((err) => {
          useMessagesStore().smartAdd(err)
          throw err
        })
    }
  }
})
